import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = ({ badgeCounts = {}, currentUser = null }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/content-analysis-dashboard',
      icon: 'BarChart3',
      badge: badgeCounts?.dashboard || 0,
      tooltip: 'Content monitoring and queue management'
    },
    {
      label: 'Content Review',
      path: '/content-review-detail',
      icon: 'FileText',
      badge: badgeCounts?.review || 0,
      tooltip: 'Detailed compliance analysis workspace'
    },
    {
      label: 'Rules Library',
      path: '/regulatory-rules-library',
      icon: 'BookOpen',
      tooltip: 'Regulatory foundation management'
    },
    {
      label: 'Reports',
      path: '/compliance-reports',
      icon: 'TrendingUp',
      tooltip: 'Audit trails and compliance analytics'
    }
  ];

  const moreMenuItems = [
    {
      label: 'Team Management',
      path: '/team-management',
      icon: 'Users',
      tooltip: 'User administration and workflow configuration'
    }
  ];

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-navigation bg-card border-b border-border">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Logo */}
          <Link to="/content-analysis-dashboard" className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Shield" size={20} color="white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">Sofron.ai</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`relative flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro hover-scale ${
                  isActiveRoute(item?.path)
                    ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={item?.tooltip}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
                {item?.badge > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-accent rounded-full">
                    {item?.badge > 99 ? '99+' : item?.badge}
                  </span>
                )}
              </Link>
            ))}

            {/* More Menu */}
            <div className="relative group">
              <Button
                variant="ghost"
                size="sm"
                iconName="MoreHorizontal"
                iconSize={16}
                className="text-muted-foreground hover:text-foreground"
              >
                More
              </Button>
              <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-state z-dropdown">
                <div className="py-1">
                  {moreMenuItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm transition-micro hover:bg-muted ${
                        isActiveRoute(item?.path)
                          ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground'
                      }`}
                      title={item?.tooltip}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* User Menu & Mobile Toggle */}
          <div className="flex items-center space-x-4">
            {/* User Context Menu */}
            {currentUser && (
              <div className="relative group hidden md:block">
                <button className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-micro">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-white">
                      {currentUser?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="hidden lg:block">{currentUser?.name}</span>
                  <Icon name="ChevronDown" size={14} />
                </button>
                <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-modal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-state z-dropdown">
                  <div className="py-1">
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border">
                      {currentUser?.role || 'User'}
                    </div>
                    <button className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-micro">
                      <Icon name="Settings" size={16} />
                      <span>Settings</span>
                    </button>
                    <button className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-micro">
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              iconName="Menu"
              iconSize={20}
              className="md:hidden text-muted-foreground hover:text-foreground"
              onClick={toggleMobileMenu}
            >
            </Button>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-mobile-overlay md:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={closeMobileMenu}></div>
          <div className="fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-modal">
            <div className="flex items-center justify-between h-16 px-6 border-b border-border">
              <span className="text-lg font-semibold text-foreground">Menu</span>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                iconSize={20}
                className="text-muted-foreground hover:text-foreground"
                onClick={closeMobileMenu}
              >
              </Button>
            </div>
            <nav className="p-4 space-y-2">
              {[...navigationItems, ...moreMenuItems]?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`relative flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium transition-micro min-touch-target ${
                    isActiveRoute(item?.path)
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                  {item?.badge > 0 && (
                    <span className="ml-auto flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-accent rounded-full">
                      {item?.badge > 99 ? '99+' : item?.badge}
                    </span>
                  )}
                </Link>
              ))}
              
              {currentUser && (
                <>
                  <div className="border-t border-border my-4"></div>
                  <div className="px-4 py-2 text-xs text-muted-foreground">
                    Signed in as {currentUser?.name} ({currentUser?.role})
                  </div>
                  <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-micro min-touch-target">
                    <Icon name="Settings" size={20} />
                    <span>Settings</span>
                  </button>
                  <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-micro min-touch-target">
                    <Icon name="LogOut" size={20} />
                    <span>Sign Out</span>
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;